import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "@/features/auth/store/sessionSlice";
import { SubscriptionRequestModal } from "@/features/subscription-request/components/SubscriptionRequestModal";
import { useActivePromotionsQuery } from "@/features/subscription-request/hooks/useActivePromotionsQuery";
import { useUpsellOfferQuery } from "@/features/subscription-request/hooks/useUpsellOfferQuery";
import type { ActivePromotion, UpsellPlanPricing } from "@/features/subscription-request/types/subscriptionRequest.types";
import { useOrganizationPlan } from "@/shared/hooks/useOrganizationFeatures";
import type { UpsellMetadata } from "@/shared/types/common";
import { filterAvailableUpsellItems } from "@/shared/utils/upsell.utils";
import BenefitsGrid from "./components/BenefitsGrid";
import BenefitsSection from "./components/BenefitsSection";
import ContactSection from "./components/ContactSection";
import HeroSection from "./components/HeroSection";
import UpgradeSection from "./components/UpgradeSection";
import { filterAvailableUpgradeFeatures, PREMIUM_PLAN_FEATURES, STANDARD_PLAN_FEATURES } from "./configs/upgradeFeatures.config";
import type { PlanTier } from "./types/upsellPage.types";

const PLAN_LABEL_BY_CODE: Record<"standard" | "premium", string> = {
  standard: "Pack Standard",
  premium: "Pack Premium",
};

type UpsellPageProps = {
  metadata: UpsellMetadata
  ctaLabel?: string
  planName?: string
  targetTier?: PlanTier
};

const UpsellPage = ({
  metadata,
  ctaLabel = "Découvrir les fonctionnalités",
  planName = "Pack Standard",
  targetTier = "standard",
}: UpsellPageProps) => {
  const { plan } = useOrganizationPlan(false);
  const user = useSelector(selectUser);
  const organizationId = user?.representative?.organization_id ?? null;
  const representative = user?.representative ?? null;

  const { data: activePromotionsData } = useActivePromotionsQuery(organizationId);
  const { data: upsellOfferData } = useUpsellOfferQuery(organizationId);

  const promotionsByCode = useMemo(() => {
    const map: Partial<Record<"standard" | "premium", ActivePromotion | null>> = {};
    activePromotionsData?.promotions.forEach((promo) => {
      const code = promo.target_plan.code;
      // DB stores Standard plan as "packcom" but the UI uses "standard"
      if (code === "standard" || code === "packcom") {
        map.standard = promo;
      }
      else if (code === "premium") {
        map.premium = promo;
      }
    });
    return map;
  }, [activePromotionsData]);

  const pricingsByCode = useMemo(() => {
    const map: Partial<Record<"standard" | "premium", UpsellPlanPricing | undefined>> = {};
    upsellOfferData?.pricings.forEach((pricing) => {
      // DB stores Standard plan as "packcom" but the API/UI uses "standard"
      if (pricing.plan_code === "standard" || pricing.plan_code === "packcom") {
        map.standard = pricing;
      }
      else if (pricing.plan_code === "premium") {
        map.premium = pricing;
      }
    });
    return map;
  }, [upsellOfferData]);

  const [openModalForCode, setOpenModalForCode] = useState<"standard" | "premium" | null>(null);

  const representativeFullName = representative
    ? `${representative.first_name ?? ""} ${representative.last_name ?? ""}`.trim()
    : "";

  const planHighlightsByCode = useMemo(() => ({
    standard: filterAvailableUpgradeFeatures(STANDARD_PLAN_FEATURES, plan?.features).map((f) => f.label),
    premium: filterAvailableUpgradeFeatures(PREMIUM_PLAN_FEATURES, plan?.features).map((f) => f.label),
  }), [plan?.features]);

  return (
    <div className="flex min-h-[80vh] flex-col bg-white">
      <HeroSection
        planName={planName}
        title={metadata.title}
        description={metadata.description}
        ctaLabel={ctaLabel}
        targetTier={targetTier}
      />

      <BenefitsGrid features={filterAvailableUpsellItems(metadata.features, plan?.features)} />

      {metadata.benefits && metadata.benefits.length > 0 && (
        <BenefitsSection
          title={metadata.benefitsTitle ?? "Les bénéfices pour le CSE et les salariés"}
          intro={metadata.benefitsIntro}
          benefits={filterAvailableUpsellItems(metadata.benefits, plan?.features)}
        />
      )}

      <UpgradeSection
        features={plan?.features}
        currentPlanName={plan?.name ?? "Actuel"}
        targetTier={targetTier}
        promotionsByCode={promotionsByCode}
        pricingsByCode={pricingsByCode}
        onSubscribe={organizationId ? setOpenModalForCode : undefined}
      />

      <ContactSection
        targetPlanName={planName}
        targetTier={targetTier}
        pageFeatureTitle={metadata.title}
        premiumFeaturesCount={filterAvailableUpgradeFeatures(PREMIUM_PLAN_FEATURES, plan?.features).length}
        onSubscribe={organizationId ? setOpenModalForCode : undefined}
      />

      {organizationId && openModalForCode && (
        <SubscriptionRequestModal
          open
          onOpenChange={(open) => !open && setOpenModalForCode(null)}
          organizationId={organizationId}
          organizationName={representative?.organization?.name ?? ""}
          representativeFullName={representativeFullName}
          targetPlanCode={openModalForCode}
          targetPlanName={PLAN_LABEL_BY_CODE[openModalForCode]}
          promotion={promotionsByCode[openModalForCode] ?? null}
          pricing={pricingsByCode[openModalForCode]}
          planHighlights={planHighlightsByCode[openModalForCode]}
        />
      )}
    </div>
  );
};

export default UpsellPage;
